/**
 * Switch — an on/off toggle for settings that take effect as a state, not an action
 * (Auto-run, Shadow mode, Follow). ADS anatomy: 40×24 (default) / 32×16 (compact)
 * track, white thumb, blue-bold track when on, neutral-bold when off. A real
 * `role="switch"` button so it is keyboard- and screen-reader-correct.
 *
 * `label` renders beside the track (clicking it toggles too); omit it and pass
 * `ariaLabel` when the context already names the control (e.g. a table column).
 */
import { Show, type JSX } from "solid-js";
import { cn } from "./utils/cn";
import { focusRing } from "./utils/focus-ring";

export function Switch(props: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  ariaLabel?: string;
  disabled?: boolean;
  /** Shows a busy state and blocks input while a save is in flight. */
  loading?: boolean;
  size?: "default" | "compact";
  class?: string;
}): JSX.Element {
  const compact = () => props.size === "compact";
  const blocked = () => !!props.disabled || !!props.loading;

  return (
    <label
      class={cn(
        "inline-flex select-none items-center gap-100",
        blocked() ? "cursor-not-allowed" : "cursor-pointer",
        props.class,
      )}
    >
      <button
        type="button"
        role="switch"
        aria-checked={props.checked}
        aria-label={props.label ? undefined : props.ariaLabel}
        aria-busy={props.loading ? "true" : undefined}
        disabled={blocked()}
        onClick={() => props.onChange(!props.checked)}
        class={cn(
          "relative inline-flex shrink-0 items-center rounded-full transition-colors",
          compact() ? "h-4 w-8" : "h-6 w-10",
          props.checked
            ? "bg-[var(--c9-bg-brand-bold)] hover:bg-[var(--c9-bg-brand-bold-hovered)]"
            : "bg-[var(--c9-bg-neutral-bold)] hover:bg-[var(--c9-bg-neutral-bold-hovered)]",
          blocked() && "opacity-60",
          props.loading && "animate-pulse",
          focusRing,
        )}
      >
        <span
          aria-hidden="true"
          class={cn(
            "inline-block rounded-full bg-[var(--c9-surface)] shadow-sm transition-transform",
            compact() ? "size-3" : "size-4",
            compact()
              ? props.checked ? "translate-x-[18px]" : "translate-x-[2px]"
              : props.checked ? "translate-x-[20px]" : "translate-x-[4px]",
          )}
        />
      </button>
      <Show when={props.label}>
        <span class="text-body font-[570] text-[color:var(--c9-text)]">{props.label}</span>
      </Show>
    </label>
  );
}
